import React from 'lib/react';
import { EVGrid, EVErrorOutline } from 'lib/components';
import Typography from 'commonComponents/typography';
import { useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import { getCurrencySymbol } from 'features/order/Order.utils';
import DashboardRetry from 'features/dashboard/DashboardRetry.component';
import SubscriptionDashboardStyles from './SubscriptionDashboardStyles';
import HistoricUsage from './components';
import {
  PROGRESS_COLORS,
  PROGRESS_MAX_ANGLE,
  calculateDaysRemaining,
  calculateUsageMetrics,
  formatCurrency,
  formatDateForDisplay,
  reverseHistoricUsage,
} from './SubscriptionDashboard.utils';

function SubscriptionDashboard() {
  const classes = SubscriptionDashboardStyles();
  const { t } = useTranslation();

  const { currencyTypeId, subscriptionDashboardData, subscriptionDashboardFailure } = useSelector(({ userData }) => ({
    currencyTypeId: userData.initParameters?.currencyTypeId,
    subscriptionDashboardData: userData.subscriptionDashboard,
    subscriptionDashboardFailure: userData.error.subscriptionDashboard,
  }));

  const historicUsage = reverseHistoricUsage(subscriptionDashboardData.historicUsage);

  const daysRemaining = calculateDaysRemaining();
  const {
    isOverLimit,
    overage,
    progressAngle,
    progressColor,
    reportsRemaining,
  } = calculateUsageMetrics(subscriptionDashboardData.monthlyQtyUsed, subscriptionDashboardData.monthlyUptoQty);

  return (
    <EVGrid className={classes.subscriptionDashboard}>
      <Typography variant="h2-400" fontColor="charcoal-900">
        {t('subscription_dashboard__subscription_plan')}
      </Typography>

      {subscriptionDashboardFailure
        ? (
          <DashboardRetry
            retryText={t('subscription_dashboard__subscription_dashboard_retry_msg')}
            action="LOAD_SUBSCRIPTION_DASHBOARD"
          />
        )
        : (
          <>
            <EVGrid className={classes.datesContainer}>
              <EVGrid className={classes.dateItem}>
                <Typography variant="subtitle-400" fontColor="charcoal-600">
                  {t('subscription_dashboard__effective_date')}
                </Typography>
                <Typography variant="subtitle-500" fontColor="charcoal-800">
                  {formatDateForDisplay(subscriptionDashboardData.effectiveDate)}
                </Typography>
              </EVGrid>
              <EVGrid className={classes.dateItem}>
                <Typography variant="subtitle-400" fontColor="charcoal-600">
                  {t('subscription_dashboard__end_date')}
                </Typography>
                <Typography variant="subtitle-500" fontColor="charcoal-800">
                  {formatDateForDisplay(subscriptionDashboardData.endDate)}
                </Typography>
              </EVGrid>
            </EVGrid>

            <EVGrid className={classes.usageContainer}>
              <Typography variant="body-700" fontColor="charcoal-800">
                {`${t('subscription_dashboard__monthly_usage')}: ${subscriptionDashboardData.monthlyQtyUsed}`}
              </Typography>

              <EVGrid className={classes.usageStatsContainer}>
                <EVGrid className={classes.usageProgressContainer}>
                  <EVGrid className={classes.progressSection}>
                    <EVGrid className={classes.progressContainer}>
                      <EVGrid
                        className={classes.progressBar}
                        style={{
                          background: `conic-gradient(from 270deg, 
                    ${progressColor} 0deg, 
                    ${progressColor} ${progressAngle}deg, 
                    ${PROGRESS_COLORS.BACKGROUND} ${progressAngle}deg, 
                    ${PROGRESS_COLORS.BACKGROUND} ${PROGRESS_MAX_ANGLE}deg)`,
                          clipPath: 'ellipse(90% 70% at 50% 0%)',
                        }}
                      />
                    </EVGrid>
                    <EVGrid className={classes.progressValueContainer}>
                      <Typography
                        style={{ fontSize: '40px', lineHeight: '40px', fontWeight: '700' }}
                        fontColor="charcoal-900"
                      >
                        {parseInt(isOverLimit ? subscriptionDashboardData.monthlyUptoQty : subscriptionDashboardData.monthlyQtyUsed, 10)}
                      </Typography>
                      <Typography variant="subtitle-400" fontColor="charcoal-700">
                        of
                        {' '}
                        {parseInt(subscriptionDashboardData.monthlyUptoQty, 10)}
                      </Typography>
                    </EVGrid>
                    {isOverLimit && (
                    <EVGrid className={classes.overageBadge}>
                      <Typography variant="h3-600" fontColor="salt-100">
                        +
                        {parseInt(overage, 10)}
                      </Typography>
                      <Typography variant="label-600" fontColor="salt-100">
                        {t('subscription_dashboard__overage')}
                      </Typography>
                    </EVGrid>
                    )}
                  </EVGrid>

                </EVGrid>

                <EVGrid className={classes.usageDetailsContainer}>
                  <EVGrid className={classes.usageDetailsItem}>
                    <Typography variant="body-600" fontColor="charcoal-700">
                      {parseInt(reportsRemaining, 10)}
                    </Typography>
                    <Typography variant="subtitle-400" fontColor="charcoal-700">
                      {t('subscription_dashboard__reports_remaining')}
                    </Typography>
                  </EVGrid>
                  <EVGrid className={classes.usageDetailsItem}>
                    <Typography variant="body-600" fontColor="charcoal-700">
                      {daysRemaining}
                    </Typography>
                    <Typography variant="subtitle-400" fontColor="charcoal-700">
                      {t('subscription_dashboard__days_remaining')}
                    </Typography>
                  </EVGrid>
                </EVGrid>
              </EVGrid>

              {isOverLimit && (
              <EVGrid className={classes.warningContainer}>
                <EVErrorOutline className={classes.warningIcon} />
                <Typography variant="subtitle-400" fontColor="charcoal-900">
                  {t('subscription_dashboard__exceeded_usage_warning')}
                </Typography>
              </EVGrid>
              )}
            </EVGrid>

            <EVGrid className={classes.pricingContainer}>
              <EVGrid className={classes.pricingItem}>
                <Typography variant="subtitle-400" fontColor="charcoal-800">
                  {t('subscription_dashboard__monthly_subscription_amount')}
                </Typography>
                <Typography variant="h3-600" fontColor="charcoal-900">
                  {getCurrencySymbol(currencyTypeId)}
                  {formatCurrency(subscriptionDashboardData.monthlySubscriptionAmount)}
                </Typography>
              </EVGrid>
              <EVGrid className={classes.pricingDivider} />
              <EVGrid className={classes.pricingItem}>
                <Typography variant="subtitle-400" fontColor="charcoal-800">
                  {t('subscription_dashboard__total_savings')}
                </Typography>
                <Typography variant="body-500" fontColor="charcoal-900">
                  {getCurrencySymbol(currencyTypeId)}
                  {formatCurrency(subscriptionDashboardData.totalSavings)}
                </Typography>
              </EVGrid>
            </EVGrid>

            {historicUsage?.length > 0 && (
            <HistoricUsage
              historicUsage={historicUsage}
              totalAllowed={parseInt(subscriptionDashboardData.monthlyUptoQty, 10)}
            />
            )}
          </>
        )}
    </EVGrid>
  );
}

export default SubscriptionDashboard;
